import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import KaboodleToaster from "components/common/KaboodleToaster";
import Table from "components/Table/Table";
import { commonColumnProps } from "constants/TableConstants";
import { omit } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useGetDocumentsQuery } from "services/documents-api";
import { useGetBackgroundJobsQuery } from "services/background-job-api";
import {
  useCreateMedicalRecordIndexMutation,
  useDeleteMedicalRecordIndexMutation,
  useGetMedicalRecordIndexesQuery,
  useUpdateMedicalRecordIndexMutation,
} from "services/future-medical-api";

import { getServiceField } from "../../future-medical-utils";
import type { MedicalHistoryTableProps } from "../medicalRecordUtils";
import {
  createEllipsisCellRenderer,
  createStatusIndicatorCell,
  getErrorMessage,
  getMedicalRecordStatusColor,
  statusColumnProps,
} from "../medicalRecordUtils";
import MedicalRecordModal from "./MedicalRecordModal";
import CopyMedicalRecordsFromMetadocButton from "./CopyMedicalRecordsFromMetadocButton";

import type {
  MedicalRecordIndexRow,
  MedicalRecordIndexPayload,
} from "./MedicalRecordModal";
import type { ApiError } from "types/ApiTypes";
import type { PaginatedResponse } from "types/DataTypes";

// clickable page number component that opens the PDF preview
const PageNumberLink = ({
  documentId,
  pageNumber,
  fileId,
}: {
  documentId: number;
  pageNumber: number;
  fileId: number;
}) => {
  const { data: documents, isLoading } = useGetDocumentsQuery(
    {
      fileId: fileId.toString(),
      filters: {
        id: documentId.toString(),
      },
    },
    {
      skip: false,
    },
  );

  // find the exact document by ID
  const documentData = documents?.results?.find((doc) => doc.id === documentId);
  const previewAvailable = !!(
    documentData?.preview_proxy_url || documentData?.proxy_url
  );

  const handleClick = () => {
    if (!documentData) return;
    if (!documentData.preview_proxy_url && !documentData.proxy_url) return;

    const url = documentData.preview_proxy_url || documentData.proxy_url;
    const key = documentData.preview_s3_key || documentData.s3_key;

    // ensure we have both the URL and key before opening
    if (!url || !key) return;

    window.open(
      `/previews?key=${encodeURIComponent(key)}&url=${encodeURIComponent(url)}&page=${pageNumber}`,
      "_blank",
    );
  };

  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        handleClick();
      }}
      style={{
        cursor: previewAvailable && !isLoading ? "pointer" : "not-allowed",
        opacity: previewAvailable && !isLoading ? 1 : 0.5,
        color: previewAvailable && !isLoading ? "#3c1283" : "inherit",
        textDecoration: previewAvailable && !isLoading ? "underline" : "none",
      }}
      title={
        isLoading
          ? "Loading document..."
          : previewAvailable
            ? "Click to preview"
            : "Preview not available"
      }
    >
      {pageNumber}
    </span>
  );
};

const MedicalRecordTable = ({ service }: MedicalHistoryTableProps) => {
  const [isMedicalRecordModalOpen, setIsMedicalRecordModalOpen] =
    useState(false);
  const [selectedMedicalRecord, setSelectedMedicalRecord] = useState<
    MedicalRecordIndexRow | undefined
  >();

  const medicalRecordColumns = [
    {
      ...statusColumnProps,
      cellClass: (row: MedicalRecordIndexRow) =>
        getMedicalRecordStatusColor(row.status || ""),
      renderCell: () => createStatusIndicatorCell(),
      ...commonColumnProps,
      width: "5%",
      minWidth: 60,
      maxWidth: 60,
      resizable: false,
    },
    {
      key: "page_number",
      name: "Page #",
      width: "8%",
      minWidth: 70,
      resizable: true,
      renderCell: ({ row }: { row: MedicalRecordIndexRow }) => {
        if (!row.document) return row.page_number;

        return (
          <PageNumberLink
            documentId={row.document}
            pageNumber={row.page_number}
            fileId={service.file.id}
          />
        );
      },
    },
    {
      key: "date",
      name: "Date",
      width: "14%",
      minWidth: 120,
      resizable: true,
    },
    {
      key: "type",
      name: "Type",
      width: "35%",
      minWidth: 200,
      resizable: true,
      renderCell: createEllipsisCellRenderer<MedicalRecordIndexRow>("type"),
    },
    {
      key: "providers",
      name: "Provider",
      width: "38%",
      minWidth: 200,
      resizable: true,
      renderCell:
        createEllipsisCellRenderer<MedicalRecordIndexRow>("providers"),
    },
  ] as const;

  const serviceId = service.id.toString();
  const fileId = service?.file?.id;

  const {
    data: medicalRecordData,
    isLoading,
    refetch,
  } = useGetMedicalRecordIndexesQuery(
    {
      file_id: fileId?.toString(),
    },
    {
      skip: !serviceId,
    },
  );

  const { data: bgJobsData } = useGetBackgroundJobsQuery(
    {
      filters: {
        file_id: fileId ? String(fileId) : "",
        limit: 10,
        offset: 0,
      },
    },
    {
      skip: !fileId,
      pollingInterval: 3000,
    },
  );

  const hasActiveJob = useMemo(() => {
    return Boolean(
      bgJobsData?.results?.some(
        (job) =>
          (job.job_type === "pay_history_extraction" ||
            job.job_type === "pay_history_analysis" ||
            job.job_type === "Pay History Extraction" ||
            job.job_type === "Pay History Analysis") &&
          (job.status === "pending" || job.status === "in_progress"),
      ),
    );
  }, [bgJobsData]);

  const [prevHasActiveJob, setPrevHasActiveJob] = useState(false);

  useEffect(() => {
    if (prevHasActiveJob && !hasActiveJob) {
      refetch();
    }
    setPrevHasActiveJob(hasActiveJob);
  }, [hasActiveJob, prevHasActiveJob, refetch]);

  const [createMedicalRecord] = useCreateMedicalRecordIndexMutation();
  const [updateMedicalRecord] = useUpdateMedicalRecordIndexMutation();
  const [deleteMedicalRecord] = useDeleteMedicalRecordIndexMutation();

  const handleCreateMedicalRecord = async (data: MedicalRecordIndexPayload) => {
    try {
      await createMedicalRecord({
        data: {
          ...data,
          ...getServiceField(service, false),
          organization: service.organization,
          file: service.file.id,
        },
      }).unwrap();

      setIsMedicalRecordModalOpen(false);
      KaboodleToaster.success("Medical record created successfully");
    } catch (error) {
      console.error("Failed to create medical record:", error);
      const errorMessage = getErrorMessage(
        error as ApiError,
        "Failed to create medical record",
      );
      KaboodleToaster.error(errorMessage);
    }
  };

  const handleUpdateMedicalRecord = async (data: MedicalRecordIndexPayload) => {
    if (!selectedMedicalRecord) return;

    try {
      await updateMedicalRecord({
        id: selectedMedicalRecord.id.toString(),
        data: omit(data, ["msa", "cost_projection"]),
      }).unwrap();

      setIsMedicalRecordModalOpen(false);
      setSelectedMedicalRecord(undefined);
      KaboodleToaster.success("Medical record updated successfully");
    } catch (error) {
      console.error("Failed to update medical record:", error);
      const errorMessage = getErrorMessage(
        error as ApiError,
        "Failed to update medical record",
      );
      KaboodleToaster.error(errorMessage);
    }
  };

  const handleDeleteMedicalRecord = async (id: string) => {
    try {
      await deleteMedicalRecord({
        id,
        ...getServiceField(service, true),
      }).unwrap();

      KaboodleToaster.success("Medical record deleted successfully");
    } catch (error) {
      console.error("Delete Error:", error);
      const errorMessage = getErrorMessage(
        error as ApiError,
        "Failed to delete medical record",
      );
      KaboodleToaster.error(errorMessage);
    }
  };

  const handleMedicalRecordRowClick = (row: MedicalRecordIndexRow) => {
    setSelectedMedicalRecord(row);
    setIsMedicalRecordModalOpen(true);
  };

  const handleAddButtonClick = () => {
    setSelectedMedicalRecord(undefined);
    setIsMedicalRecordModalOpen(true);
  };

  const handleModalClose = () => {
    setIsMedicalRecordModalOpen(false);
    setSelectedMedicalRecord(undefined);
  };

  return (
    <>
      <div
        className="data-block-header flex ai-c jc-sb"
        style={{ padding: "4px 12px 9px 12px", margin: 0 }}
      >
        <div className="swim-headers">Medical Record Review</div>
        <div className="flex ai-c f-none" style={{ gap: "8px" }}>
          <CopyMedicalRecordsFromMetadocButton
            service={service}
            hasExistingRecords={
              ((medicalRecordData as PaginatedResponse<MedicalRecordIndexRow>)
                ?.results?.length ?? 0) > 0
            }
          />
          <ButtonIcon
            kind="neutral"
            size="small"
            onClick={handleAddButtonClick}
          >
            add_circle
          </ButtonIcon>
        </div>
      </div>

      <div className="table-container-right">
        <Table<MedicalRecordIndexRow>
          className="msa-medical-record"
          columns={medicalRecordColumns}
          rows={
            (medicalRecordData as PaginatedResponse<MedicalRecordIndexRow>)
              ?.results || []
          }
          rowKeyGetter={(row) => row.id.toString()}
          isLoading={isLoading}
          onSelectedRowsChange={() => {}}
          onCellClick={({ row }) => handleMedicalRecordRowClick(row)}
          isResizable
          isSortable
        />
      </div>

      <MedicalRecordModal
        isOpen={isMedicalRecordModalOpen}
        onClose={handleModalClose}
        onSubmit={
          selectedMedicalRecord
            ? handleUpdateMedicalRecord
            : handleCreateMedicalRecord
        }
        onDelete={handleDeleteMedicalRecord}
        initialData={selectedMedicalRecord}
        title={
          selectedMedicalRecord ? "Edit Medical Record" : "Add Medical Record"
        }
        service={service}
      />
    </>
  );
};

export default MedicalRecordTable;
